<script setup lang="ts">
/**
 * The admin landing page: does anything need me today?
 *
 * Eleven tabs, and the only way to learn whether one wanted you was to open
 * it — ten of them reported nothing at rest. Every number here was already
 * being computed inside some tab; none of it was visible from outside.
 *
 * The page is deliberately almost empty when things are fine. A quiet
 * morning should look quiet: one sentence saying so, not a wall of green
 * tiles reassuring you. Tiles that always render train people to stop
 * reading them, and then the one day a tile turns red it reads as furniture
 * too. So nothing is shown unless it is asking for something.
 *
 * Each source loads independently and fails silently: a 403 or a backend
 * hiccup drops one line, it does not take the page down. What it cannot
 * count it says it cannot count, rather than reporting zero — a silent zero
 * here would read as "all clear", which is the one lie this page must not
 * tell.
 *
 * Items are grouped by who has to act (`utils/todo.ts`), because the admin
 * who reads this page is rarely the person who can close every item: a
 * signature is the signers', a risk class is the AI Board's. Each item
 * names what it is about, and every name is a link to where it is done.
 */
import { computed, onMounted, ref } from "vue";
import {
  type DataSource,
  getDataSources,
  getFeedbackOverview,
  getGovernance,
  getResponsibilities,
  listAiRequests,
} from "../api/admin";
import {
  type Actor,
  type TodoItem,
  assistantTodos,
  groupByActor,
  sourceTodos,
} from "../utils/todo";
import { adminLink } from "../utils/vocabulary";
import SkeletonRows from "./SkeletonRows.vue";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{ devUser: string }>();

const items = ref<TodoItem[]>([]);
const context = ref("");
const unavailable = ref<string[]>([]);
const loading = ref(false);

function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}

async function load(): Promise<void> {
  loading.value = true;
  const found: TodoItem[] = [];
  const missing: string[] = [];
  const ctx: string[] = [];

  const [gov, sources, requests, feedback, roster] = await Promise.allSettled([
    getGovernance(props.devUser, 1),
    getDataSources(props.devUser),
    listAiRequests(props.devUser, "pending"),
    getFeedbackOverview("", props.devUser),
    getResponsibilities(props.devUser),
  ]);
  who.value = {
    signers:
      sources.status === "fulfilled"
        ? pendingSigners(sources.value.sources)
        : [],
    "data-owner":
      roster.status === "fulfilled"
        ? unique(
            roster.value.roles
              .filter((r) => r.kind === "data-owner")
              .flatMap((r) => r.people.map((p) => p.name)),
          )
        : [],
    board:
      roster.status === "fulfilled"
        ? unique(
            roster.value.roles
              .filter((r) => r.kind === "board")
              .flatMap((r) => r.people.map((p) => p.name)),
          )
        : [],
  };

  if (gov.status === "fulfilled") {
    found.push(...assistantTodos(gov.value.assistants));
    const n = gov.value.assistants.length;
    ctx.push(`${n} ${plural(n, "assistant")}`);
  } else missing.push("assistants");

  if (sources.status === "fulfilled") {
    found.push(...sourceTodos(sources.value.sources));
    const n = sources.value.sources.length;
    ctx.push(`${n} ${plural(n, "data source")}`);
  } else missing.push("data sources");

  if (requests.status === "fulfilled") {
    const n = requests.value.length;
    if (n)
      found.push({
        key: "ai-requests",
        actor: "board",
        n,
        text: `AI ${plural(n, "request")} awaiting a decision`,
        tone: "warn",
        link: adminLink("ai-requests"),
        targets: [],
        compliance: false,
      });
  } else missing.push("the AI Board queue");

  if (feedback.status === "fulfilled") {
    const n = feedback.value.totals.new_down;
    if (n)
      found.push({
        key: "feedback",
        actor: "owners",
        n,
        text: `${plural(n, "thumbs-down")} not yet reviewed`,
        tone: "warn",
        link: adminLink("feedback-review"),
        targets: [],
        compliance: false,
      });
  } else missing.push("feedback");

  items.value = found;
  context.value = ctx.join(" · ");
  unavailable.value = missing;
  loading.value = false;
}

/** Who is behind each group, by name, so "Agreement signers" is people. */
const who = ref<Partial<Record<Actor, string[]>>>({});

function unique(xs: string[]): string[] {
  return [...new Set(xs.filter(Boolean))];
}

/** The people whose signature an unsigned agreement is still waiting for. */
function pendingSigners(sources: DataSource[]): string[] {
  return unique(
    sources
      .filter((s) => s.agreement_state === "draft")
      .flatMap((s) => s.agreement_meta?.signers ?? [])
      .filter((x) => !x.signed_on)
      .flatMap((x) =>
        x.name ? [x.name] : (x.people ?? []).map((p) => p.name),
      ),
  );
}

onMounted(load);

const groups = computed(() => groupByActor(items.value));
const total = computed(() => items.value.reduce((s, i) => s + i.n, 0));

/** Names under an item: a few, then a count — the list is a way in, not
 *  a second copy of the tab. */
const SHOW = 6;
const opened = ref<Set<string>>(new Set());
function shown(it: TodoItem) {
  return opened.value.has(it.key) ? it.targets : it.targets.slice(0, SHOW);
}
function more(key: string): void {
  opened.value = new Set([...opened.value, key]);
}
</script>

<template>
  <div class="overview">
    <SkeletonRows v-if="loading" :rows="4" />
    <template v-else>
      <p v-if="items.length === 0" class="clear">
        Nothing needs anyone right now.
      </p>
      <template v-else>
        <h2 class="headline">
          To do · {{ total }} {{ total === 1 ? "item" : "items" }}
        </h2>
        <section v-for="g in groups" :key="g.actor.key" class="group">
          <h3 class="group__head">
            {{ g.actor.label }}
            <span class="group__hint">{{
              who[g.actor.key]?.length
                ? who[g.actor.key]!.join(", ")
                : g.actor.hint
            }}</span>
          </h3>
          <ul class="items">
            <li v-for="it in g.items" :key="it.key" class="item">
              <a class="item__main" :href="it.link">
                <StatusChip :tone="it.tone" dot>{{ it.n }}</StatusChip>
                <span class="item__text">{{ it.text }}</span>
                <span class="item__go" aria-hidden="true">→</span>
              </a>
              <div v-if="it.targets.length" class="item__targets">
                <a v-for="t in shown(it)" :key="t.link" :href="t.link">{{
                  t.label
                }}</a>
                <button
                  v-if="it.targets.length > SHOW && !opened.has(it.key)"
                  class="link"
                  @click="more(it.key)"
                >
                  +{{ it.targets.length - SHOW }} more
                </button>
              </div>
            </li>
          </ul>
        </section>
      </template>

      <p v-if="context" class="context">{{ context }}</p>
      <p v-if="unavailable.length" class="context context--warn">
        Could not check {{ unavailable.join(", ") }} — those may need attention
        without showing here.
      </p>
    </template>
  </div>
</template>

<style scoped>
.overview {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  max-width: 44rem;
}
.headline {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 650;
}
.clear {
  margin: 0;
  font-size: 1.05rem;
  color: var(--color-muted);
}
.items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.group__head {
  margin: 0.5rem 0 0;
  font-size: 0.92rem;
  font-weight: 600;
}
.group__hint {
  margin-left: 0.4rem;
  font-weight: 400;
  font-size: 0.8rem;
  color: var(--color-muted);
}
.item {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}
.item:hover {
  border-color: var(--color-accent);
}
.item__main {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  text-align: left;
  font: inherit;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}
.item__targets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0.9rem;
  padding: 0 0.75rem 0.5rem 2.6rem;
  font-size: 0.82rem;
}
.item__targets a {
  color: var(--color-accent);
  text-decoration: none;
}
.item__targets a:hover {
  text-decoration: underline;
}
button.link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--color-muted);
  cursor: pointer;
}
.item__text {
  flex: 1;
}
.item__go {
  color: var(--color-muted);
}
.context {
  margin: 0;
  font-size: 0.82rem;
  color: var(--color-muted);
}
.context--warn {
  color: var(--color-warn-ink);
}
</style>
